'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Plus } from 'lucide-react';
import { getApiUrl } from '@/utils';

/**
 * 게시물 데이터 인터페이스
 * 
 * 공지사항, 채용공고, 뉴스레터의 기본 게시물 정보를 정의합니다.
 */
interface Post {
  id: string;
  title: string;
  createdAt: string;
  isNew: boolean;
}

/**
 * 모든 게시물 데이터 인터페이스
 * 
 * 세 가지 타입의 게시물을 포함하는 데이터 구조를 정의합니다.
 */
interface AllPosts {
  notices: Post[];
  recruits: Post[];
  newsletters: Post[];
}

/**
 * 탭 타입 정의
 * 
 * 공지사항, 채용공고, 뉴스레터 중 선택할 수 있는 탭을 정의합니다.
 */
type TabType = 'notices' | 'recruits' | 'newsletters';

/**
 * NoticeSection 컴포넌트 Props 인터페이스
 * 
 * 공지사항 섹션에 표시할 게시물 데이터를 정의합니다.
 */
interface NoticeSectionProps {
  allPosts?: AllPosts; // 선택적 prop으로 변경
}

/**
 * 공지사항 섹션 컴포넌트
 * 
 * 홈페이지에 공지사항, 채용공고, 뉴스레터를 탭으로 구분하여 표시하는 컴포넌트입니다.
 * 각 탭별로 최신 게시물 5개를 보여주며, 전체 목록으로 이동할 수 있는 링크를 제공합니다.
 * 
 * 주요 기능:
 * - 탭 기반 게시물 분류 (공지사항, 채용공고, 뉴스레터)
 * - 최신 게시물 5개 표시
 * - NEW 태그로 최신 게시물 강조
 * - 반응형 디자인 (모바일/데스크톱)
 * - 전체 목록 이동 링크
 * - 데이터 없음 상태 처리
 */
export function NoticeSection({ allPosts }: NoticeSectionProps) {
  const [activeTab, setActiveTab] = useState<TabType>('notices');
  const [posts, setPosts] = useState<AllPosts | null>(allPosts || null);
  const [loading, setLoading] = useState(!allPosts); // allPosts가 없을 경우에만 로딩 상태

  // allPosts prop이 없을 경우 API에서 데이터를 가져옴
  useEffect(() => {
    if (!allPosts) {
      const fetchPosts = async () => {
        try {
          setLoading(true);
          const response = await fetch(getApiUrl('home/all-posts'));

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setPosts(result.data);
            }
          }
        } catch (error) {
          console.error('Failed to fetch posts:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchPosts();
    }
  }, [allPosts]);

  /**
   * 탭 클릭 핸들러
   * 
   * @param tabType - 선택할 탭 타입
   */
  const handleTabClick = (tabType: TabType) => {
    setActiveTab(tabType);
  };

  /**
   * 탭 타입에 따른 링크 URL을 반환하는 함수
   * 
   * @param tabType - 탭 타입
   * @returns 해당 탭의 전체 목록 페이지 URL
   */
  const getTabLink = (tabType: TabType) => {
    switch (tabType) {
      case 'notices':
        return '/notices';
      case 'recruits':
        return '/recruit';
      case 'newsletters':
        return '/newsletters';
    }
  };

  /**
   * 탭 타입에 따른 한국어 라벨을 반환하는 함수
   * 
   * @param tabType - 탭 타입
   * @returns 해당 탭의 한국어 라벨
   */
  const getTabLabel = (tabType: TabType) => {
    switch (tabType) {
      case 'notices':
        return '공지사항';
      case 'recruits':
        return '채용공고';
      case 'newsletters':
        return '뉴스레터';
    }
  };

  /**
   * 현재 활성 탭의 게시물 목록을 반환하는 함수
   * 
   * @returns 현재 탭에 해당하는 게시물 배열
   */
  const getCurrentPosts = (): Post[] => {
    if (!posts) return [];

    switch (activeTab) {
      case 'notices':
        return posts.notices || [];
      case 'recruits':
        return posts.recruits || [];
      case 'newsletters':
        return posts.newsletters || [];
      default:
        return [];
    }
  };

  const currentPosts = getCurrentPosts();

  return (
    <div className='flex flex-col gap-2 sm:gap-4 lg:col-span-3'>
      {/* 섹션 헤더 */}
      <div className="flex flex-col gap-1 sm:gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-5">
          {/* 모바일 헤더 */}
          <div className="flex items-center justify-between sm:hidden">
            <h2 className="text-2xl font-extrabold text-primary">NOTICE</h2>
            <Link
              href={getTabLink(activeTab)}
              className="text-muted hover:text-foreground transition-colors duration-100 w-9 h-9 -mr-2 flex items-center justify-center"
            >
              <Plus className="size-8 stroke-2" />
            </Link>
          </div>

          {/* 데스크톱 헤더 */}
          <div className="hidden sm:flex sm:items-center sm:gap-5">
            <h2 className="text-3xl font-extrabold text-primary">NOTICE</h2>
            {/* 데스크톱 탭 버튼들 */}
            <div className="flex gap-2">
              <button
                onClick={() => handleTabClick('notices')}
                className={`text-lg lg:text-xl font-bold px-3 py-2 transition-colors duration-100 ${activeTab === 'notices'
                  ? 'text-foreground'
                  : 'text-foreground/50 hover:text-primary'
                  }`}
              >
                공지사항
              </button>
              <button
                onClick={() => handleTabClick('recruits')}
                className={`text-lg lg:text-xl font-bold px-3 py-2 transition-colors duration-100 ${activeTab === 'recruits'
                  ? 'text-foreground'
                  : 'text-foreground/50 hover:text-primary'
                  }`}
              >
                채용공고
              </button>
              <button
                onClick={() => handleTabClick('newsletters')}
                className={`text-lg lg:text-xl font-bold px-3 py-2 transition-colors duration-100 ${activeTab === 'newsletters'
                  ? 'text-foreground'
                  : 'text-foreground/50 hover:text-primary'
                  }`}
              >
                뉴스레터
              </button>
            </div>
          </div>

          {/* 데스크톱 전체보기 링크 */}
          <Link
            href={getTabLink(activeTab)}
            className="hidden sm:flex text-muted hover:text-foreground transition-colors duration-100 w-11 h-11 -mr-2 items-center justify-center"
          >
            <Plus className="size-8 stroke-2" />
          </Link>
        </div>

        {/* 모바일 탭 버튼들 */}
        <div className="flex gap-1 sm:hidden">
          <button
            onClick={() => handleTabClick('notices')}
            className={`text-lg font-bold px-2 py-1.5 transition-colors duration-100 ${activeTab === 'notices'
              ? 'text-foreground'
              : 'text-foreground/50 hover:text-primary'
              }`}
          >
            공지사항
          </button>
          <button
            onClick={() => handleTabClick('recruits')}
            className={`text-lg font-bold px-2 py-1.5 transition-colors duration-100 ${activeTab === 'recruits'
              ? 'text-foreground'
              : 'text-foreground/50 hover:text-primary'
              }`}
          >
            채용공고
          </button>
          <button
            onClick={() => handleTabClick('newsletters')}
            className={`text-lg font-bold px-2 py-1.5 transition-colors duration-100 ${activeTab === 'newsletters'
              ? 'text-foreground'
              : 'text-foreground/50 hover:text-primary'
              }`}
          >
            뉴스레터
          </button>
        </div>
      </div>

      {/* 게시물 목록 영역 */}
      <div className="px-4 py-2 border-2 border-border rounded-xl sm:rounded-2xl overflow-hidden h-[296px] sm:h-[336px] shadow-md sm:shadow-lg">
        {currentPosts.length > 0 ? (
          // 게시물 목록 렌더링
          currentPosts.map((item, index) => (
            <Link
              key={item.id}
              href={`${getTabLink(activeTab)}/${item.id}`}
              className={`group flex items-center justify-between cursor-pointer px-2 sm:px-2 py-3 sm:py-5 border-b border-border h-[56px] sm:h-[64px] ${index === 4 ? 'border-b-0' : ''
                }`}
            >
              {/* 게시물 제목 영역 */}
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                {/* NEW 태그 */}
                {item.isNew && (
                  <span className="bg-primary text-white text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex-shrink-0">NEW</span>
                )}
                {/* 게시물 제목 */}
                <span className="group-hover:underline text-foreground text-base sm:text-lg truncate transition-colors duration-100">
                  {item.title}
                </span>
              </div>
              {/* 작성일 */}
              <span className="text-secondary text-base sm:text-lg whitespace-nowrap ml-2 sm:ml-3 flex-shrink-0">
                {new Date(item.createdAt).toLocaleDateString('ko-KR')}
              </span>
            </Link>
          ))
        ) : (
          // 게시물이 없는 경우 안내 메시지
          <div className="flex items-center justify-center h-full text-foreground">
            <p className="text-center">
              <span className="block text-xl font-bold mb-1">{getTabLabel(activeTab)} 게시글이 없습니다.</span>
              <span className="text-sm text-muted">새로운 게시글이 등록되면 여기에 표시됩니다.</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}